import { FC, useContext, useLayoutEffect, useRef, useState } from 'react';

import { Separator } from '@smartshell/ui-kit';
import { createPortal } from 'react-dom';

import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';
import { formatSum } from 'utils';

import s from './CompositePaymentTooltip.module.scss';

interface IPaymentItemsTooltipProps {
  total: number;
  card: number;
  cash: number;
}
export const CompositePaymentTooltip: FC<IPaymentItemsTooltipProps> = ({
  total,
  card,
  cash,
}) => {
  const { t } = useContext(LocalizationContext);

  const {
    state: {
      settings: {
        currency: { sign: currency },
      },
    },
  } = useContext(SettingsContext);
  const tooltipTimer = useRef<NodeJS.Timeout | null>(null);
  const openerRef = useRef<HTMLSpanElement>(null);
  const [tooltipContainerEl, setTooltipContainerEl] =
    useState<HTMLDivElement | null>(null);

  const [isVisible, setIsVisible] = useState(false);

  useLayoutEffect(() => {
    if (!isVisible || !openerRef.current) return;

    const { top, left } = openerRef.current.getBoundingClientRect();

    const el = document.createElement('div');
    el.classList.add(s.tooltip);
    el.style.top = `${top - 8}px`;
    el.style.left = `${left - 8}px`;

    document.body.appendChild(el);
    setTooltipContainerEl(el);

    return () => {
      document.body.removeChild(el);
    };
  }, [isVisible, openerRef.current]);

  useLayoutEffect(() => {
    if (!tooltipContainerEl) return;

    const { top, height } = tooltipContainerEl.getBoundingClientRect();

    if (window.innerHeight <= top + height) {
      tooltipContainerEl.style.top = `${window.innerHeight - height - 8}px`;
    }
  }, [tooltipContainerEl]);

  return (
    <span
      ref={openerRef}
      className={s.category}
      onMouseEnter={() => {
        if (tooltipTimer.current) {
          clearTimeout(tooltipTimer.current);
          tooltipTimer.current = null;
        }

        setIsVisible(true);
      }}
      onMouseLeave={() => {
        tooltipTimer.current = setTimeout(() => setIsVisible(false), 50);
      }}
    >
      {formatSum(total, currency, { maximumFractionDigits: 2 })}
      {tooltipContainerEl &&
        createPortal(
          <div className={s.tooltipContent}>
            <div className={s.row}>
              <span>{t('total')}</span>
              {formatSum(total, currency, { maximumFractionDigits: 2 })}
            </div>
            <Separator />
            <div className={s.composite}>
              <div className={s.row}>
                <span>{t('money.card')}</span>
                {formatSum(card, currency, { maximumFractionDigits: 2 })}
              </div>
              <div className={s.row}>
                <span>{t('money.cash')}</span>
                {formatSum(cash, currency, { maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>,
          tooltipContainerEl
        )}
    </span>
  );
};
